import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, ArrowLeft, Building2, Globe, Mail, Phone, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Imprint() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => i18n.changeLanguage(isEnglish ? 'fi' : 'en')}
              >
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'FI' : 'EN'}
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold">
              {isEnglish ? 'Company Information' : 'Yritystiedot'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {isEnglish ? 'Legal information as required by Finnish law' : 'Suomen lain edellyttämät tiedot'}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Company Details Card */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-xl font-heading font-semibold mb-4">
              <Building2 className="h-5 w-5 text-primary" />
              {isEnglish ? 'Company Details' : 'Yrityksen tiedot'}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Company Name' : 'Yrityksen nimi'}</p>
                <p className="font-medium">Vera Value Oy</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Business ID (Y-tunnus)' : 'Y-tunnus'}</p>
                <p className="font-medium">3323295-9</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'VAT Number' : 'ALV-numero'}</p>
                <p className="font-medium">FI33232959</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Location' : 'Sijainti'}</p>
                <p className="font-medium">Mäkelä, Finland</p>
              </div>
            </div>
          </div>

          {/* Contact Card */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-xl font-heading font-semibold mb-4">
              <Mail className="h-5 w-5 text-primary" />
              {isEnglish ? 'Contact Information' : 'Yhteystiedot'}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Email' : 'Sähköposti'}</p>
                <a href="mailto:admin@evenrounds.com" className="font-medium text-primary hover:underline">
                  admin@evenrounds.com
                </a>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Website' : 'Verkkosivusto'}</p>
                <p className="font-medium">evenrounds.com</p>
              </div>
            </div>
          </div>

          {/* Registration Card */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-xl font-heading font-semibold mb-4">
              <FileText className="h-5 w-5 text-primary" />
              {isEnglish ? 'Registration' : 'Rekisteröinti'}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Trade Register' : 'Kaupparekisteri'}</p>
                <p className="font-medium">{isEnglish ? 'Finnish Patent and Registration Office (PRH)' : 'Patentti- ja rekisterihallitus (PRH)'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Company Form' : 'Yhtiömuoto'}</p>
                <p className="font-medium">{isEnglish ? 'Limited Liability Company (Oy)' : 'Osakeyhtiö (Oy)'}</p>
              </div>
            </div>
          </div>

          {/* Responsible Person Card */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-xl font-heading font-semibold mb-4">
              <Globe className="h-5 w-5 text-primary" />
              {isEnglish ? 'Online Service' : 'Verkkopalvelu'}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Service Name' : 'Palvelun nimi'}</p>
                <p className="font-medium">Even Rounds</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isEnglish ? 'Service Type' : 'Palvelun tyyppi'}</p>
                <p className="font-medium">{isEnglish ? 'Yarn Inventory Management & Digital Pattern Shop' : 'Lankavaraston hallinta & Digitaalinen mallikauppa'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-12 bg-stone-50 rounded-2xl p-6">
          <h2 className="text-xl font-heading font-semibold mb-4">
            {isEnglish ? 'Legal Documents' : 'Oikeudelliset asiakirjat'}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Link 
              to="/privacy" 
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-stone-100 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{isEnglish ? 'Privacy Policy' : 'Tietosuojaseloste'}</span>
            </Link>
            <Link 
              to="/terms" 
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-stone-100 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{isEnglish ? 'Terms of Service' : 'Käyttöehdot'}</span>
            </Link>
            <Link 
              to="/cookies" 
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-stone-100 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{isEnglish ? 'Cookie Policy' : 'Evästekäytäntö'}</span>
            </Link>
            <Link 
              to="/refund" 
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-stone-100 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{isEnglish ? 'Refund Policy' : 'Peruutusehdot'}</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Vera Value Oy. {isEnglish ? 'All rights reserved.' : 'Kaikki oikeudet pidätetään.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
